import React, { useCallback, useMemo } from 'react';
import { BarChartItemPayload, Box, costFormater, Text, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { FULL_MONTH_FORMAT, FULL_YEAR_FORMAT, RAW_DATE_FORMAT, SHORT_MONTH_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { BalanceNotionalBreakdownPayload } from '../EmployerClaimSpend.types';
interface Params {
  disabled?: boolean;
}
const useGetClaimSpendQuery = (planId: string, params?: Params) => {
  const { isLoading, data, isError, refetch, isSuccess } = useQuery(
    [API_URL.ACCOUNT_USAGE_PLANS_BALANCES_NOTIONAL_BY_PLAN_ID(''), planId],
    () => api.get(API_URL.ACCOUNT_USAGE_PLANS_BALANCES_NOTIONAL_BY_PLAN_ID(planId)),
    {
      retry: 0,
      enabled: !!planId && !params?.disabled,
    }
  );

  const mappedData = useMemo<BalanceNotionalBreakdownPayload | null>(() => {
    const currentData = data?.data;
    if (!currentData) {
      return null;
    }

    const item: BalanceNotionalBreakdownPayload = {
      total: currentData?.total_claim_spend || 0,
      monthlyBreakdown:
        currentData?.monthly_breakdown?.map((item) => ({
          month: item.month ? dayjs(item.month, RAW_DATE_FORMAT).toDate() : null,
          spend: item.claim_spend || 0,
        })) || [],
    };
    return item;
  }, [data]);

  const { formattedData, currentIndex } = useMemo(() => {
    const breakdownData = mappedData?.monthlyBreakdown;
    // Track the current-month bar's position within the final array (year
    // delimiters and the optional trailing "current month" bar all shift
    // indices, so we resolve it here rather than from the ambiguous 'MMM'
    // labels — repeated month names in >12-month plans made the chart
    // auto-scroll to the plan's valid_from start instead (EL-31918)).
    let resolvedIndex = -1;
    const result: BarChartItemPayload[] =
      breakdownData?.reduce((result: BarChartItemPayload[], item, index) => {
        const last = breakdownData[index - 1];

        const years: BarChartItemPayload[] = [];

        if (last && !dayjs(item.month).isSame(dayjs(last.month), 'year')) {
          years.push({
            value: 0,
            label: item.month ? dayjs(item.month).format(FULL_YEAR_FORMAT) : '',
            isDelimiter: true,
            hint: '',
          });
        }

        // The real bar for this month lands after any year delimiter we just
        // inserted; record its index when it is the current month.
        if (item.month && dayjs(item.month).isSame(dayjs(), 'month')) {
          resolvedIndex = result.length + years.length;
        }

        return [
          ...result,
          ...years,
          {
            value: item.spend || 0,
            label: item.month ? dayjs(item.month).format(SHORT_MONTH_FORMAT) : '',
            hint: (
              <Box direction="row">
                <Text size="small" weight={700}>
                  {item.month ? dayjs(item.month).format(FULL_MONTH_FORMAT) : ''}:
                </Text>

                <Text size="small" weight={500} margin={{ left: 'spacing4' }}>
                  {!item.spend
                    ? dayjs(item.month).isBefore(dayjs())
                      ? 'No data'
                      : 'No data yet'
                    : `${costFormater(item.spend, {
                        isPrecision: true,
                      })}`}
                </Text>
              </Box>
            ),
          },
        ];
      }, []) || [];
    const last = _.last(breakdownData);
    if (
      last &&
      !dayjs().isSame(dayjs(last.month), 'month') &&
      dayjs().subtract(1, 'month').isSame(dayjs(last.month), 'month')
    ) {
      // This synthetic trailing bar represents the current month; it becomes
      // the auto-scroll target.
      resolvedIndex = result.length;
      result.push({
        value: 0,
        label: dayjs().format(SHORT_MONTH_FORMAT),
        isDelimiter: false,
        hint: (
          <Box direction="row">
            <Text size="small" weight={700}>
              {dayjs().format(FULL_MONTH_FORMAT)}:
            </Text>

            <Text size="small" weight={500} margin={{ left: 'spacing4' }}>
              No data yet
            </Text>
          </Box>
        ),
      });
    }
    return { formattedData: result, currentIndex: resolvedIndex };
  }, [mappedData?.monthlyBreakdown]);

  const total = useMemo(() => {
    return data?.data?.total_claim_spend || 0;
  }, [data?.data?.total_claim_spend]);

  const handleRefetch = useCallback(() => {
    if (!isLoading) {
      refetch();
    }
  }, [isLoading, refetch]);
  return { isLoading, data: formattedData, isError, refetch: handleRefetch, total, currentIndex, isSuccess };
};
export default useGetClaimSpendQuery;
